import { ModalBody, ModalFoot, ModalHead } from '@dixa/lib-shared-habla';
import { AxiosError } from 'axios';
import { Queue } from 'lib-shared-legacy-types';
import noop from 'lodash/noop';
import { useForm } from 'react-hook-form';

import { TwitterAccount, twitterApi, TwitterToken } from 'apis/twitter';
import { HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';

type TwitterConnectProps = {
  defaultQueue: Queue;
  twitterToken: TwitterToken;
  onConnect: (account: TwitterAccount) => void;
  onCloseModal: VoidFunction;
};

type FieldValues = {
  pin: string;
};

const formId = 'twitter-connect-form';

export const TwitterConnect = ({
  onConnect,
  onCloseModal,
  twitterToken,
  defaultQueue,
}: TwitterConnectProps): JSX.Element => {
  const notify = useNotifications();

  const [createAccount, { isLoading }] = twitterApi.useCreateAccountMutation();

  const form = useForm<FieldValues>({
    mode: 'all',
    defaultValues: {
      pin: '',
    },
  });

  const handleFormSubmit = async (values: FieldValues) => {
    try {
      const { auth_token, auth_token_secret } = twitterToken;

      const { data: account } = await createAccount({
        auth_token,
        auth_token_secret,
        verifier: values.pin,
        queueId: defaultQueue.id,
      }).unwrap();

      onConnect(account);
    } catch (e) {
      const error = e as AxiosError;

      notify(
        {
          title:
            error?.response?.status === 406
              ? 'Invalid Twitter PIN - please try again'
              : 'An error occurred when trying to connect the Twitter account.',
        },
        NotificationType.Error,
      );
    }
  };

  return (
    <>
      <ModalHead title="Add Twitter account" onClose={onCloseModal} />

      <ModalBody>
        <form
          id={formId}
          noValidate
          tabIndex={-1}
          onSubmit={form.handleSubmit(handleFormSubmit)}
        >
          <HookFormText
            name="pin"
            label="Pin"
            control={form.control}
            rules={{
              required: 'Pin is required',
            }}
            componentProps={{
              autoFocus: true,
            }}
            helpIcon={{
              text: 'Authorize Twitter and enter the received pin here',
            }}
            noAlert
          />
        </form>
      </ModalBody>

      <ModalFoot
        cancelOnClick={onCloseModal}
        confirmName="Authorize"
        confirmOnClick={noop}
        confirmProps={{
          type: 'submit',
          isLoading,
          disabled: !form.formState.isValid || form.formState.isSubmitting,
          form: formId,
        }}
      />
    </>
  );
};
